/**
 * PulseFit / RoxKit Application Coordinator
 * 
 * Core application orchestrator responsible for bootstrapping,
 * Supabase authentication session lifecycle, and modular view coordination.
 */

function escapeHtml(unsafe) {
  if (typeof unsafe !== 'string') return unsafe;
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

// Telemetry & Performance Module Imports
import { CIRCLE_RADIUS, CIRCLE_CIRCUMFERENCE } from './modules/telemetry/index.js';
import {
  STATION_META,
  HYROX_STATIONS,
  isGenericStageName,
  normalizeStationName,
  parseSequentialStationsFromNotes,
  isRawStationSequenceNotes,
  resolveBestWorkoutNotes,
  getStationBadgeHtml,
  calculateWorkoutZoneSeconds,
  scrubPII,
  reportError,
  initErrorTelemetry,
  getQueuedTelemetryErrors,
  clearQueuedTelemetryErrors
} from './modules/telemetry/index.js';
import {
  getWorkoutEffortScore,
  getWorkoutSufferScore,
  getWorkoutTimestamp,
  calculateBanisterTimeSeries
} from './modules/performance/index.js';

// Hardware Bridges & Integration Imports
import {
  normalizeHealthKitWorkout,
  requestHealthKitWorkouts as requestNativeHealthKitWorkouts,
  requestHealthKitSummary,
  initHealthKitBridge,
  startFavoriteSimulation,
  isBiometricSupported,
  triggerBiometricAuth,
  initBiometricBridge,
  getWorkoutTotalDistance
} from './modules/integrations/index.js';


// AI Coaching & Export Module Imports
import { callGeminiCoach, runAIEvaluation, processVoiceCommandWithGemini, generateTelemetryPrompt, generateAIComparisonPrompt, generateSummaryPrompt, generateDictationPrompt, generateRecommendationPrompt } from './modules/ai-coaching/index.js';
import { renderInstagramCard } from './modules/export/index.js';

// Central Reactive Proxy Store
import {
  store,
  subscribe,
  subscribeKey,
  loadStateFromStorage,
  saveStateToStorage,
  setWorkouts,
  resetStore,
  getInitialState,
  getMondayOfCurrentWeek
} from './store.js';

import { saveOfflineWorkout, getOfflineWorkouts, deleteOfflineWorkout, initCloudClient } from './modules/storage/index.js';

// Domain View Controllers & Router
import {
  showView,
  setupNavigation,
  getPreviousViewId,
  initRouter,
  renderDashboard,
  renderWeeklyTrend,
  renderStationTiles,
  renderAchievements,
  setupQuickAdjustmentButtons,
  initDashboardView,
  renderWorkouts,
  navigateWeek,
  updateWeekUI,
  filterWorkouts,
  setupWorkoutsSearch,
  deleteWorkout,
  isDateInCurrentWeek,
  getWeeklyDateRangeString,
  initWorkoutsView,
  initReleaseNotesView,
  openWorkoutDetail,
  closeWorkoutDetail,
  renderDetailStagesEditor,
  saveWorkoutSplits,
  saveWorkoutNotes,
  generateAISummary,
  setupExpandedWorkoutNotesModal,
  initDictateAssistant,
  openStationAlternativesModal,
  closeStationAlternativesModal,
  confirmDeleteWorkout,
  getStationAlternatives,
  saveStationAlternatives,
  renderStationAlternativesChips,
  setupStationAlternativesEditor,
  setupWorkoutDetail,
  setupModal,
  initEditorView,
  openExpandedHeartRateModal,
  setupExpandedHeartRateModal,
  openShareCardModal,
  setupShareCardModal,
  renderStagesHeartRateChart,
  renderExpandedStagesHeartRateChart,
  updateExpandedSegmentHUD,
  renderHeartRateZonesUI,
  renderHeartRateChart,
  renderExpandedHeartRateChart,
  renderPerformanceView,
  calculatePerformanceMetrics,
  saveDailySubjectiveRecovery,
  getDailySubjectiveRecovery,
  createSafeChart,
  renderBanisterChart,
  renderWeeklySufferChart,
  renderMonthlySufferChart,
  renderPerformanceTrends,
  initPerformanceView,
  populateSettingsGoalsFields,
  populateHeartRateThresholds,
  populateGeminiFields,
  saveSettings,
  setupGoalsEditor,
  setupGemini,
  updateStravaVisibility,
  setupStrava,
  updateStravaUI,
  exchangeStravaCode,
  refreshStravaToken,
  syncStravaActivities,
  openDeleteAccountDialog,
  closeDeleteAccountDialog,
  executeAccountDeletion,
  initSettingsView,
  isStravaAdmin,
  updateStravaPostButtonState,
  updateWorkoutInCloud,
  handlePostWorkoutToStrava,
  openWorkoutComparison,
  generateAIComparison,
  initComparisonView,
  clearWorkoutSelection,
  toggleWorkoutSelection,
  updateSummarizeButtonState,
  getSelectedWorkoutIds,
  openPlanRoutineModal,
  closePlanRoutineModal,
  addPlanSplit,
  loadHyroxPresetIntoPlanner,
  clearPlanSplits,
  updatePlanSplitField,
  stepPlanSplitField,
  movePlanSplit,
  duplicatePlanSplit,
  removePlanSplit,
  recalculatePlanTotals,
  savePlannedWorkout,
  deletePlannedWorkout,
  sendPlannedWorkoutToWatch,
  renderSavedPlannedWorkouts,
  selectDashboardPlannedWorkout,
  toggleDashboardPlansDrawer,
  setDashboardPlanSearchQuery,
  setModalPlanSearchQuery,
  generateWorkoutRecommendation,
  initPlannerView,
  getDraftPlanSplits,
  setDraftPlanSplits,
  getCurrentEditingPlanId
} from './modules/views/index.js';

// Transparent Store Alias
const state = store;
if (typeof window !== 'undefined') {
  window.state = store;
  window.clearAllLocalUserData = clearAllLocalUserData;
  window.fetchUserWorkouts = fetchUserWorkouts;
  window.openShareCardModal = openShareCardModal;
  window.setupShareCardModal = setupShareCardModal;
  window.startFavoriteSimulation = startFavoriteSimulation;
  window.openWorkoutComparison = openWorkoutComparison;
  window.generateAIComparison = generateAIComparison;
  window.initComparisonView = initComparisonView;
  window.clearWorkoutSelection = clearWorkoutSelection;
  window.toggleWorkoutSelection = toggleWorkoutSelection;
  window.updateSummarizeButtonState = updateSummarizeButtonState;
  window.saveWorkoutNotes = saveWorkoutNotes;
  window.generateAISummary = generateAISummary;
  window.setupExpandedWorkoutNotesModal = setupExpandedWorkoutNotesModal;
  window.initDictateAssistant = initDictateAssistant;
  window.runAIEvaluation = runAIEvaluation;
  window.processVoiceCommandWithGemini = processVoiceCommandWithGemini;
  window.updateStravaPostButtonState = updateStravaPostButtonState;
  window.updateWorkoutInCloud = updateWorkoutInCloud;
  window.handlePostWorkoutToStrava = handlePostWorkoutToStrava;
  window.postWorkoutToStrava = handlePostWorkoutToStrava;
  window.setupStrava = setupStrava;
  window.updateStravaUI = updateStravaUI;
  window.exchangeStravaCode = exchangeStravaCode;
  window.refreshStravaToken = refreshStravaToken;
  window.syncStravaActivities = syncStravaActivities;
  window.openPlanRoutineModal = openPlanRoutineModal;
  window.closePlanRoutineModal = closePlanRoutineModal;
  window.addPlanSplit = addPlanSplit;
  window.loadHyroxPresetIntoPlanner = loadHyroxPresetIntoPlanner;
  window.clearPlanSplits = clearPlanSplits;
  window.updatePlanSplitField = updatePlanSplitField;
  window.stepPlanSplitField = stepPlanSplitField;
  window.movePlanSplit = movePlanSplit;
  window.duplicatePlanSplit = duplicatePlanSplit;
  window.removePlanSplit = removePlanSplit;
  window.recalculatePlanTotals = recalculatePlanTotals;
  window.savePlannedWorkout = savePlannedWorkout;
  window.deletePlannedWorkout = deletePlannedWorkout;
  window.sendPlannedWorkoutToWatch = sendPlannedWorkoutToWatch;
  window.renderSavedPlannedWorkouts = renderSavedPlannedWorkouts;
  window.selectDashboardPlannedWorkout = selectDashboardPlannedWorkout;
  window.toggleDashboardPlansDrawer = toggleDashboardPlansDrawer;
  window.setDashboardPlanSearchQuery = setDashboardPlanSearchQuery;
  window.setModalPlanSearchQuery = setModalPlanSearchQuery;
  window.generateWorkoutRecommendation = generateWorkoutRecommendation;
  window.initPlannerView = initPlannerView;
}


// --- Supabase Integration & Auth Coordinator ---
const VITE_SUPABASE_URL = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SUPABASE_URL) || '';
const VITE_SUPABASE_ANON_KEY = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SUPABASE_ANON_KEY) || '';

if (typeof window !== 'undefined') {
  window.SUPABASE_URL = VITE_SUPABASE_URL;
  window.SUPABASE_ANON_KEY = VITE_SUPABASE_ANON_KEY;
}

let supabaseClient = (typeof window !== 'undefined' && window.supabaseClient)
  ? window.supabaseClient
  : initCloudClient();

if (typeof window !== 'undefined') {
  window.supabaseClient = supabaseClient;
}

export function saveState() {
  saveStateToStorage();
}

export async function saveUserSettingsToCloud() {
  if (!supabaseClient || !state.currentUser || state.isGuest) return;
  try {
    const payload = {
      goals: state.goals, weeklyZoneGoals: state.weeklyZoneGoals,
      weeklyAdjustments: state.weeklyAdjustments, weeklyZoneAdjustments: state.weeklyZoneAdjustments,
      geminiApiKey: state.geminiApiKey, geminiInstructions: state.geminiInstructions,
      strava: state.strava, showRunWorkouts: state.showRunWorkouts,
      stationAlternatives: state.stationAlternatives,
      plannedWorkouts: Array.isArray(state.plannedWorkouts) ? state.plannedWorkouts : []
    };
    await supabaseClient.from('profiles').update({
      avatar_url: JSON.stringify(payload), updated_at: new Date().toISOString()
    }).eq('id', state.currentUser.id);
  } catch (e) {
    console.warn("saveUserSettingsToCloud failed:", e);
  }
}

if (typeof window !== 'undefined') {
  window.saveUserSettingsToCloud = saveUserSettingsToCloud;
}

export async function ensureUserProfileExists(user) {
  if (!supabaseClient || !user) return;
  try {
    const { data, error } = await supabaseClient.from('profiles').select('*').eq('id', user.id).maybeSingle();
    if (error) return console.error("Error checking user profile:", error);
    if (!data) {
      const username = user.email ? user.email.split('@')[0] : 'User';
      const meta = user.user_metadata || {};
      await supabaseClient.from('profiles').insert({
        id: user.id, username, display_name: meta.full_name || username,
        dob: meta.dob || null,
        gender: meta.gender || null,
        weight_kg: meta.weight_kg ? parseFloat(meta.weight_kg) : null,
        height_cm: meta.height_cm ? parseFloat(meta.height_cm) : null,
        fitness_level: meta.fitness_level || null,
        primary_goal: meta.primary_goal || null,
        created_at: new Date().toISOString(), updated_at: new Date().toISOString()
      });
    } else if (data.avatar_url) {
      try {
        const settings = JSON.parse(data.avatar_url);
        if (settings.goals) state.goals = { ...state.goals, ...settings.goals };
        if (settings.weeklyZoneGoals) state.weeklyZoneGoals = { ...state.weeklyZoneGoals, ...settings.weeklyZoneGoals };
        if (settings.weeklyAdjustments) state.weeklyAdjustments = { ...state.weeklyAdjustments, ...settings.weeklyAdjustments };
        if (settings.weeklyZoneAdjustments) state.weeklyZoneAdjustments = { ...state.weeklyZoneAdjustments, ...settings.weeklyZoneAdjustments };
        if (settings.geminiApiKey) state.geminiApiKey = settings.geminiApiKey;
        if (settings.geminiInstructions) state.geminiInstructions = settings.geminiInstructions;
        if (settings.strava && typeof settings.strava === 'object') {
          const localStrava = state.strava || {};
          const cloudStrava = settings.strava;
          const useCloudTokens = (Number(cloudStrava.expiresAt || 0) >= Number(localStrava.expiresAt || 0)) || !localStrava.accessToken;
          state.strava = {
            ...localStrava,
            ...cloudStrava,
            clientId: String(cloudStrava.clientId || localStrava.clientId || '').trim(),
            clientSecret: String(cloudStrava.clientSecret || localStrava.clientSecret || '').trim(),
            redirectUri: cloudStrava.redirectUri || localStrava.redirectUri || (typeof window !== 'undefined' && window.location ? window.location.origin : 'http://localhost:8080'),
            syncMode: cloudStrava.syncMode || localStrava.syncMode || 'push_only',
            athleteName: cloudStrava.athleteName || localStrava.athleteName || '',
            accessToken: useCloudTokens ? (cloudStrava.accessToken || localStrava.accessToken || '') : (localStrava.accessToken || cloudStrava.accessToken || ''),
            refreshToken: useCloudTokens ? (cloudStrava.refreshToken || localStrava.refreshToken || '') : (localStrava.refreshToken || cloudStrava.refreshToken || ''),
            expiresAt: Math.max(Number(cloudStrava.expiresAt || 0), Number(localStrava.expiresAt || 0))
          };
        }
        if (settings.stationAlternatives) state.stationAlternatives = { ...state.stationAlternatives, ...settings.stationAlternatives };
        if (Array.isArray(settings.plannedWorkouts)) {
          const localPlans = Array.isArray(state.plannedWorkouts) ? state.plannedWorkouts : [];
          const mergedMap = new Map();
          settings.plannedWorkouts.forEach(p => {
            if (p && p.id) mergedMap.set(String(p.id), p);
          });
          localPlans.forEach(p => {
            if (p && p.id) {
              const existing = mergedMap.get(String(p.id));
              if (!existing || Date.parse(p.updatedAt || '') >= Date.parse(existing.updatedAt || '')) {
                mergedMap.set(String(p.id), p);
              }
            }
          });
          state.plannedWorkouts = Array.from(mergedMap.values());
        }
        saveState();
        populateGeminiFields();
      } catch (e) {
        console.warn("Failed to parse avatar_url settings payload:", e);
      }
    }
    updateStravaVisibility();
    setupStrava();
    updateStravaUI();
  } catch (e) {
    console.error("Exception in ensureUserProfileExists:", e);
  }
}

export async function handleLogin(email, password) {
  if (!supabaseClient) throw new Error("Supabase client is not initialized!");
  const errEl = document.getElementById('auth-error');
  if (errEl) errEl.style.display = 'none';
  const { data, error } = await supabaseClient.auth.signInWithPassword({ email, password });
  if (error) {
    if (errEl) {
      if (error.message.includes('Email not confirmed')) {
        errEl.innerText = "Please confirm your email address before logging in.";
      } else {
        errEl.innerText = error.message;
      }
      errEl.style.display = 'block';
    }
    throw error;
  }
  return data;
}

export async function handleSignUp(email, password, metadata) {
  if (!supabaseClient) throw new Error("Supabase client is not initialized!");
  const errEl = document.getElementById('auth-error');
  if (errEl) errEl.style.display = 'none';
  const { data, error } = await supabaseClient.auth.signUp({
    email, password, options: { data: metadata }
  });
  if (error) {
    if (errEl) { errEl.innerText = error.message; errEl.style.display = 'block'; }
    throw error;
  }
  return data;
}

export async function handlePasswordReset(email) {
  if (!supabaseClient) throw new Error("Supabase client is not initialized!");
  const msgEl = document.getElementById('forgot-password-msg');
  if (msgEl) {
    msgEl.style.display = 'none';
    msgEl.classList.remove('error');
    msgEl.style.color = '';
  }
  const { error } = await supabaseClient.auth.resetPasswordForEmail(email, {
    redirectTo: window.location.origin,
  });
  if (error) {
    if (msgEl) {
      msgEl.innerText = error.message;
      msgEl.style.display = 'block';
      msgEl.style.color = '#ef4444';
      msgEl.style.background = 'rgba(239,68,68,0.1)';
      msgEl.style.border = '1px solid rgba(239,68,68,0.2)';
    }
    throw error;
  }
  if (msgEl) {
    msgEl.innerText = 'Check your email for the reset link!';
    msgEl.style.display = 'block';
    msgEl.style.color = '#10b981';
    msgEl.style.background = 'rgba(16,185,129,0.1)';
    msgEl.style.border = '1px solid rgba(16,185,129,0.2)';
  }
}



export function clearAllLocalUserData() {
  const isCompanion = Boolean(
    typeof window !== 'undefined' &&
    window.webkit &&
    window.webkit.messageHandlers &&
    window.webkit.messageHandlers.authHandler
  );
  if (!isCompanion || !localStorage.getItem('pulsefit_jwt')) {
    localStorage.removeItem('pulsefit_user_id');
  }
  localStorage.removeItem('roxkit_state');
  localStorage.removeItem('pulsefit_state');
  localStorage.removeItem('roxkit_guest_mode');
  localStorage.removeItem('roxfit_guest_mode');
  if (typeof sessionStorage !== 'undefined') {
    try { sessionStorage.removeItem('pulsefit_ios_auth_synced_uid'); } catch (_) {}
  }

  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith('roxkit_state') || key.startsWith('pulsefit_state'))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(k => localStorage.removeItem(k));
  resetStore();
}

export function handleLogout() {
  const wasGuest = state.isGuest;
  clearAllLocalUserData();
  if (window.webkit?.messageHandlers?.authHandler) {
    window.webkit.messageHandlers.authHandler.postMessage({ action: 'logout' });
  }
  const authView = document.getElementById('view-auth');
  const mainApp = document.getElementById('main-app-container');
  if (authView) authView.style.display = 'flex';
  if (mainApp) mainApp.style.display = 'none';
  if (!wasGuest && supabaseClient) {
    supabaseClient.auth.signOut().catch(err => console.warn("Supabase signOut error:", err));
  }
  showToast(wasGuest ? 'Signed out of guest mode' : 'Signed out successfully', 'info');
}

export async function handleGuestMode() {
  const btnAuthGuest = document.getElementById('btn-auth-guest');
  const originalText = btnAuthGuest ? btnAuthGuest.innerText : 'Continue as Guest';
  if (btnAuthGuest) {
    btnAuthGuest.disabled = true;
    btnAuthGuest.innerText = 'Connecting to Demo...';
  }
  try {
    if (!supabaseClient) throw new Error("Supabase client not initialized");
    let authRes = await supabaseClient.auth.signInWithPassword({ email: 'demo@roxfit.app', password: 'Password123!' });
    if (authRes.error) {
      authRes = await supabaseClient.auth.signInWithPassword({ email: 'demo@roxkit.app', password: 'Password123!' });
    }
    if (authRes.error) throw authRes.error;

    state.isGuest = true;
    localStorage.setItem('roxkit_guest_mode', 'true');
    showView('view-dashboard');
    showToast('Welcome to RoxKit Demo Mode! Full workouts loaded.', 'success');
  } catch (err) {
    console.warn("Live guest authentication fallback:", err.message);
    state.isGuest = true;
    state.currentUser = { id: 'guest-preview-user', email: 'demo@roxkit.app', user_metadata: { full_name: 'Guest Athlete' } };
    if (!state.workouts || state.workouts.length === 0) state.workouts = getSampleRoxKitWorkouts();
    const authView = document.getElementById('view-auth');
    const mainApp = document.getElementById('main-app-container');
    if (authView) authView.style.display = 'none';
    if (mainApp) mainApp.style.display = 'grid';
    initApp();
    showView('view-dashboard');
    showToast('Welcome to RoxKit Demo Mode! Offline sample loaded.', 'info');
  } finally {
    if (btnAuthGuest) {
      btnAuthGuest.disabled = false;
      btnAuthGuest.innerText = originalText;
    }
  }
}

export function getSampleRoxKitWorkouts() {
  const now = Date.now();
  return [{
    id: 'demo-workout-1', timestamp: now,
    date: new Date(now).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    time: new Date(now).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    type: 'RoxKit Training', duration: 62, calories: 780, distance: 8.4,
    avgHeartrate: 158, maxHeartrate: 178, notes: 'Demo Hyrox Workout', stages: []
  }];
}

export function mapDatabaseWorkout(dbw) {
  if (!dbw) return null;
  const timestamp = dbw.start_time ? Date.parse(dbw.start_time) : (dbw.created_at ? Date.parse(dbw.created_at) : Date.now());
  const dateFormatted = new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timeFormatted = new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const durationMin = Math.round((dbw.duration_seconds || 0) / 60);
  let meta = dbw.healthkit_raw_metadata || {};
  if (typeof meta === 'string') {
    try { meta = JSON.parse(meta); } catch (e) { meta = {}; }
  }
  if (!meta || typeof meta !== 'object' || Array.isArray(meta) || ('0' in meta && '1' in meta)) {
    meta = {};
  }
  const distanceKm = dbw.total_distance_meters ? (dbw.total_distance_meters / 1000) : (meta.distance || 0);
  const calories = dbw.total_energy_burned_kcal || meta.calories || Math.round(durationMin * 8.5);

  // Prepare station name recovery sources (raw metadata stages & sequential workout notes)
  let rawMetaStages = meta.stages || null;
  if (typeof rawMetaStages === 'string') {
    try { rawMetaStages = JSON.parse(rawMetaStages); } catch (e) { rawMetaStages = null; }
  }
  const notesStations = parseSequentialStationsFromNotes(
    meta.stationSequenceNotes || dbw.notes || meta.notes || ''
  );

  const recoverStageName = (currentName, stageIdxZeroBased) => {
    if (!isGenericStageName(currentName)) {
      return currentName;
    }
    if (Array.isArray(rawMetaStages)) {
      const rawMatch = rawMetaStages.find((rs, rIdx) => {
        if (!rs || typeof rs !== 'object') return false;
        const rNum = rs.stageNum || rs.stage_index || (rIdx + 1);
        return rNum === (stageIdxZeroBased + 1);
      }) || rawMetaStages[stageIdxZeroBased];
      if (rawMatch && typeof rawMatch === 'object') {
        const rawCandidate = rawMatch.stationName || rawMatch.station_name || rawMatch.name || '';
        if (!isGenericStageName(rawCandidate)) {
          return normalizeStationName(rawCandidate) || rawCandidate;
        }
      }
    }
    const notesCandidate = notesStations[stageIdxZeroBased];
    if (notesCandidate && !isGenericStageName(notesCandidate)) {
      return notesCandidate;
    }
    return currentName;
  };

  // Ingest normalized workout_stages if present, falling back to legacy JSONB stages
  let mappedStages = null;
  if (Array.isArray(dbw.workout_stages) && dbw.workout_stages.length > 0) {
    // Defensively deduplicate stages by stage_index, keeping the newest record
    const uniqueStagesMap = new Map();
    for (const st of dbw.workout_stages) {
      const idx = st.stage_index;
      if (idx === undefined || idx === null) continue;
      if (!uniqueStagesMap.has(idx)) {
        uniqueStagesMap.set(idx, st);
      } else {
        const existing = uniqueStagesMap.get(idx);
        const existingTime = existing.created_at ? Date.parse(existing.created_at) : 0;
        const newTime = st.created_at ? Date.parse(st.created_at) : 0;
        if (newTime >= existingTime) {
          uniqueStagesMap.set(idx, st);
        }
      }
    }
    const dedupedStages = Array.from(uniqueStagesMap.values());
    const sortedStages = dedupedStages.sort((a, b) => (a.stage_index || 0) - (b.stage_index || 0));
    mappedStages = sortedStages.map((st, idx) => {
      const durSecTotal = st.duration_seconds !== null && st.duration_seconds !== undefined ? Math.round(st.duration_seconds) : 0;
      const dMin = Math.floor(durSecTotal / 60);
      const dSec = durSecTotal % 60;

      const stageNum = st.stage_index || (idx + 1);
      const rawStageTypeLower = (st.stage_type || '').toLowerCase();
      const rawName = st.name || '';
      const isExplicitNonStation = rawStageTypeLower === 'run' || rawStageTypeLower === 'roxzone' || rawStageTypeLower === 'rest' || rawStageTypeLower === 'cardio';
      const defaultNonStationName = rawStageTypeLower === 'roxzone' ? 'RoxZone' : (rawStageTypeLower === 'rest' ? 'Rest' : (rawStageTypeLower === 'cardio' ? 'Cardio' : (rawStageTypeLower === 'run' ? 'Run' : '')));
      const stName = isExplicitNonStation
        ? ((!rawName || isGenericStageName(rawName)) ? defaultNonStationName : rawName)
        : recoverStageName(rawName, stageNum - 1);
      const isRox = stName.toLowerCase() === 'roxzone' || rawStageTypeLower === 'roxzone';
      const isRest = stName.toLowerCase() === 'rest' || rawStageTypeLower === 'rest';
      const isCardio = stName.toLowerCase() === 'cardio' || rawStageTypeLower === 'cardio';
      const isRun = stName.toLowerCase() === 'run' || rawStageTypeLower === 'run';

      let inferredType = st.stage_type;
      if (!inferredType || inferredType === 'Other' || (inferredType === 'Strength' && (isRox || isRest || isCardio || isRun))) {
        if (isRox) inferredType = 'RoxZone';
        else if (isRest) inferredType = 'Rest';
        else if (isCardio) inferredType = 'Cardio';
        else if (isRun) inferredType = 'Run';
        else inferredType = st.stage_type || 'Strength';
      }

      return {
        id: st.id,
        workoutId: st.workout_id,
        userId: st.user_id,
        stageNum: st.stage_index,
        name: stName,
        stationName: stName,
        type: inferredType,
        stage_type: inferredType,
        duration_seconds: durSecTotal,
        duration: durSecTotal,
        durationMin: dMin,
        durationSec: dSec,
        distance: st.distance_meters !== null && st.distance_meters !== undefined ? st.distance_meters : 0,
        distance_meters: st.distance_meters !== null && st.distance_meters !== undefined ? st.distance_meters : 0,
        avgHr: st.avg_hr !== null && st.avg_hr !== undefined ? st.avg_hr : 0,
        maxHr: st.max_hr !== null && st.max_hr !== undefined ? st.max_hr : 0,
        reps: st.reps !== null && st.reps !== undefined ? st.reps : null,
        weight: st.weight_kg !== null && st.weight_kg !== undefined ? st.weight_kg : null,
        weight_kg: st.weight_kg !== null && st.weight_kg !== undefined ? st.weight_kg : null,
        metadata: st.metadata || {}
      };
    });
  } else {
    let rawStages = rawMetaStages || dbw.stages || null;
    if (typeof rawStages === 'string') {
      try { rawStages = JSON.parse(rawStages); } catch (e) { rawStages = null; }
    }
    if (Array.isArray(rawStages) && rawStages.length > 0) {
      // Deduplicate raw stages by stage index if duplicate entries exist
      const seenRawIndices = new Set();
      const dedupedRaw = [];
      for (let i = 0; i < rawStages.length; i++) {
        const st = rawStages[i];
        const num = st.stageNum || st.stage_index || (i + 1);
        if (!seenRawIndices.has(num)) {
          seenRawIndices.add(num);
          dedupedRaw.push(st);
        }
      }
      mappedStages = dedupedRaw.map((st, idx) => {
        const durSecTotal = Number.isFinite(st.duration_seconds)
          ? Math.round(st.duration_seconds)
          : (Number.isFinite(st.duration)
            ? Math.round(st.duration)
            : (Number.isFinite(st.durationMin)
              ? Math.round((st.durationMin * 60) + (st.durationSec || 0))
              : 0));
        const dMin = st.durationMin !== undefined ? st.durationMin : Math.floor(durSecTotal / 60);
        const dSec = st.durationSec !== undefined ? st.durationSec : (durSecTotal % 60);

        const stageNum = st.stageNum || st.stage_index || (idx + 1);
        const rawType = (st.type || st.stage_type || '').toLowerCase();
        const rawStName = st.stationName || st.station_name || st.name || '';
        const isExplicitNonStation = rawType === 'run' || rawType === 'roxzone' || rawType === 'rest' || rawType === 'cardio';
        const isExplicitStation = rawType === 'station' || rawType === 'strength' || rawType.includes('functional');
        const defaultNonStationName = rawType === 'roxzone' ? 'RoxZone' : (rawType === 'rest' ? 'Rest' : (rawType === 'cardio' ? 'Cardio' : (rawType === 'run' ? 'Run' : '')));
        const stName = isExplicitNonStation
          ? ((!rawStName || isGenericStageName(rawStName)) ? defaultNonStationName : rawStName)
          : recoverStageName(rawStName, stageNum - 1);
        const trimmedName = stName.trim();
        const lowerName = trimmedName.toLowerCase();
        const isRox = lowerName.includes('rox') || rawType.includes('rox');
        const isRest = (!isExplicitStation && /^rest(?:\s+period)?$/i.test(trimmedName)) || rawType === 'rest';
        const isCardio = lowerName === 'cardio' || rawType === 'cardio';
        const isRun = (!isExplicitStation && /^(?:run|running)(?:\b|\s*:)/i.test(trimmedName)) || rawType === 'run' || rawType === 'running';

        let inferredType = 'Station';
        if (isRox) inferredType = 'RoxZone';
        else if (isRest) inferredType = 'Rest';
        else if (isCardio) inferredType = 'Cardio';
        else if (isRun) inferredType = 'Run';
        else if (st.type === 'Strength' || rawType === 'strength' || rawType.includes('functional')) inferredType = 'Station';
        else if (st.type) inferredType = st.type;

        const resolvedAvgHr = Number.isFinite(st.avgHr)
          ? Math.round(st.avgHr)
          : (Number.isFinite(st.avg_hr)
            ? Math.round(st.avg_hr)
            : (Number.isFinite(st.average_heart_rate)
              ? Math.round(st.average_heart_rate)
              : 0));

        const resolvedDistance = st.distance !== null && st.distance !== undefined
          ? st.distance
          : (st.distance_meters !== null && st.distance_meters !== undefined ? st.distance_meters : 0);

        const resolvedReps = isRox ? null : (st.reps !== null && st.reps !== undefined ? st.reps : null);

        return {
          id: st.id || `stage-${idx + 1}`,
          stageNum,
          name: stName || (isRox ? 'RoxZone' : (isRun ? 'Run' : 'Station')),
          stationName: stName || (isRox ? 'RoxZone' : (isRun ? 'Run' : 'Station')),
          type: inferredType,
          stage_type: inferredType,
          duration_seconds: durSecTotal,
          duration: durSecTotal,
          durationMin: dMin,
          durationSec: dSec,
          distance: resolvedDistance,
          distance_meters: resolvedDistance,
          avgHr: resolvedAvgHr,
          maxHr: st.maxHr !== undefined ? st.maxHr : (st.max_hr || 0),
          reps: resolvedReps,
          weight: st.weight !== null && st.weight !== undefined ? st.weight : (st.weight_kg || null),
          weight_kg: st.weight_kg !== null && st.weight_kg !== undefined ? st.weight_kg : (st.weight || null),
          metadata: st.metadata || {}
        };
      });
    }
  }

  let finalDistance = distanceKm || dbw.distance || 0;
  if (mappedStages && mappedStages.length > 0) {
    const totalRunMeters = mappedStages.reduce((acc, st) => {
      const typeLower = (st.type || st.stage_type || '').toLowerCase();
      const nameLower = (st.stationName || st.name || '').toLowerCase();
      const isRun = typeLower === 'run' || typeLower === 'running' || typeLower === 'cardio';
      const isRox = typeLower === 'roxzone' || nameLower.includes('rox');
      if ((isRun || isRox) && typeLower !== 'rest') {
        const dist = Number(st.distance_meters ?? st.distance ?? 0);
        return acc + (Number.isFinite(dist) && dist > 0 ? dist : 0);
      }
      return acc;
    }, 0);
    finalDistance = totalRunMeters > 0 ? Math.round((totalRunMeters / 1000) * 100) / 100 : 0;
  }

  let finalType = dbw.activity_type || 'Other';
  const customStr = `${dbw.notes || ''} ${meta.notes || ''} ${meta.stationSequenceNotes || ''} ${meta.customWorkoutType || ''} ${meta.custom_title || ''} ${meta.name || ''}`;
  if (/hyrox|roxkit/i.test(finalType) || /hyrox|roxkit/i.test(customStr)) {
    finalType = 'Hyrox Training';
  } else if (mappedStages && mappedStages.length > 0 && (finalType === 'Other' || finalType === 'Cardio')) {
    finalType = 'Hyrox Training';
  }

  const resolvedNotes = resolveBestWorkoutNotes(meta, dbw);
  const preservedStationSeq = meta.stationSequenceNotes ||
    (isRawStationSequenceNotes(meta.notes) ? meta.notes : (isRawStationSequenceNotes(dbw.notes) ? dbw.notes : null));
  const resolvedAiSummary = meta.aiSummary || (meta.userNotesEdited ? resolvedNotes : null);
  const isUserEdited = Boolean(
    meta.userNotesEdited ||
    (resolvedAiSummary && !isRawStationSequenceNotes(resolvedAiSummary))
  );

  return {
    id: dbw.id, timestamp, date: dateFormatted, time: timeFormatted,
    type: finalType, duration: durationMin, calories,
    notes: resolvedNotes,
    aiSummary: resolvedAiSummary,
    userNotesEdited: isUserEdited,
    notesUpdatedAt: meta.notesUpdatedAt || null,
    stationSequenceNotes: preservedStationSeq,
    stages: mappedStages, distance: finalDistance,
    avgHeartrate: meta.avgHeartrate || dbw.avgheartrate || null,
    maxHeartrate: meta.maxHeartrate || dbw.maxheartrate || null,
    elevationGain: meta.elevationGain || dbw.elevationgain || null,
    zones: meta.zones || dbw.zones || null,
    sufferScore: meta.sufferScore || dbw.sufferscore || null,
    sufferMultiplier: meta.sufferMultiplier || 1,
    excludeFromStats: meta.excludeFromStats !== undefined ? meta.excludeFromStats : (dbw.excludefromstats || false),
    isFavorite: Boolean(dbw.is_favorite || meta.isFavorite || false),
    stravaId: meta.stravaId || dbw.stravaid || null,
    stravaRunId: meta.stravaRunId || null,
    healthKitId: meta.healthKitId || null,
    rawMetadata: {
      ...meta,
      notes: resolvedNotes,
      ...(resolvedAiSummary ? { aiSummary: resolvedAiSummary } : {}),
      ...(isUserEdited ? { userNotesEdited: true } : {}),
      ...(preservedStationSeq ? { stationSequenceNotes: preservedStationSeq } : {})
    },
    source: dbw.source_name === 'HealthKit' ? 'HealthKit' : (meta.stravaId ? 'Strava' : 'Manual')
  };
}

function getWorkoutDedupeKey(w) {
  if (!w) return '';
  if (w.id) return `id:${String(w.id).toLowerCase()}`;
  if (w.healthKitId) return `hk:${String(w.healthKitId).toLowerCase()}`;
  const st = w.startTime ? (typeof w.startTime === 'object' && w.startTime.toISOString ? w.startTime.toISOString() : String(w.startTime)) : '';
  const tp = w.type || '';
  return `st:${st}|tp:${tp}`;
}

export async function hydrateWorkoutsFromOfflineFallback() {
  const offlineList = await getOfflineWorkouts().catch(() => []) || [];
  let cachedList = [];
  try {
    if (typeof localStorage !== 'undefined') {
      cachedList = JSON.parse(localStorage.getItem('pulsefit_cached_workouts') || '[]');
    }
  } catch (_e) {
    cachedList = [];
  }

  const existingWorkouts = Array.isArray(state.workouts) ? state.workouts : [];
  const mergedMap = new Map();

  for (const w of existingWorkouts) {
    const key = getWorkoutDedupeKey(w);
    if (key) mergedMap.set(key, w);
  }

  for (const w of offlineList) {
    const key = getWorkoutDedupeKey(w);
    if (key && !mergedMap.has(key)) {
      mergedMap.set(key, w);
    }
  }

  for (const w of cachedList) {
    const key = getWorkoutDedupeKey(w);
    if (key && !mergedMap.has(key)) {
      mergedMap.set(key, w);
    }
  }

  const mergedList = Array.from(mergedMap.values());
  mergedList.sort((a, b) => {
    const tA = Number(a.timestamp) || getWorkoutTimestamp(a) || 0;
    const tB = Number(b.timestamp) || getWorkoutTimestamp(b) || 0;
    return tB - tA;
  });

  if (mergedList.length > 0 || !state.workouts) {
    state.workouts = mergedList;
    saveState();
    renderDashboard();
    renderWorkouts();
    if (typeof document !== 'undefined' && document.getElementById('view-performance')?.classList.contains('active')) {
      renderPerformanceView();
    }
  }
}

export async function fetchUserWorkouts() {
  if (!supabaseClient || !state.currentUser) return;
  try {
    const { data, error } = await supabaseClient
      .from('workouts')
      .select('*, workout_stages(*)')
      .eq('user_id', state.currentUser.id)
      .order('start_time', { ascending: false });
    if (error) {
      console.error('Error fetching workouts from Supabase:', error);
      await hydrateWorkoutsFromOfflineFallback();
      showToast('Failed to fetch workouts from cloud', 'error');
      return;
    }
    const existingLocalMap = new Map();
    for (const w of (state.workouts || [])) {
      if (w && (w.id || w.healthKitId)) {
        existingLocalMap.set(String(w.id || w.healthKitId).toLowerCase(), w);
      }
    }

    const mappedCloudWorkouts = (data || []).map(dbw => {
      const cloudW = mapDatabaseWorkout(dbw);
      if (!cloudW) return cloudW;
      const key = String(cloudW.id || cloudW.healthKitId || '').toLowerCase();
      const localW = existingLocalMap.get(key);
      if (localW && !(localW.rawMetadata && '0' in localW.rawMetadata && '1' in localW.rawMetadata)) {
        const localHasRichNotes = (localW.userNotesEdited || (localW.aiSummary && !isRawStationSequenceNotes(localW.aiSummary))) &&
          localW.notes && !isRawStationSequenceNotes(localW.notes);
        const cloudHasRawNotes = !cloudW.notes || isRawStationSequenceNotes(cloudW.notes);
        const localIsNewer = (localW.notesUpdatedAt || 0) > (cloudW.notesUpdatedAt || 0);

        if (localHasRichNotes && (cloudHasRawNotes || localIsNewer)) {
          const preservedSeq = cloudW.stationSequenceNotes || localW.stationSequenceNotes || (isRawStationSequenceNotes(cloudW.notes) ? cloudW.notes : null);
          cloudW.notes = localW.notes;
          cloudW.aiSummary = localW.aiSummary || localW.notes;
          cloudW.userNotesEdited = true;
          cloudW.notesUpdatedAt = localW.notesUpdatedAt || Date.now();
          if (preservedSeq) cloudW.stationSequenceNotes = preservedSeq;
          cloudW.rawMetadata = {
            ...(cloudW.rawMetadata || {}),
            notes: cloudW.notes,
            aiSummary: cloudW.aiSummary,
            userNotesEdited: true,
            notesUpdatedAt: cloudW.notesUpdatedAt,
            ...(preservedSeq ? { stationSequenceNotes: preservedSeq } : {})
          };
        }
      }
      return cloudW;
    });

    // Merge unsynced offline workouts not yet present in cloud response
    const offlineWorkouts = await getOfflineWorkouts().catch(() => []) || [];
    const cloudKeySet = new Set();
    for (const cw of mappedCloudWorkouts) {
      if (cw.id) cloudKeySet.add(`id:${String(cw.id).toLowerCase()}`);
      if (cw.healthKitId) cloudKeySet.add(`hk:${String(cw.healthKitId).toLowerCase()}`);
      const k = getWorkoutDedupeKey(cw);
      if (k) cloudKeySet.add(k);
    }

    const unsyncedOffline = [];
    for (const ow of offlineWorkouts) {
      const owId = ow.id ? `id:${String(ow.id).toLowerCase()}` : null;
      const owHk = ow.healthKitId ? `hk:${String(ow.healthKitId).toLowerCase()}` : null;
      const owKey = getWorkoutDedupeKey(ow);
      const alreadyInCloud = (owId && cloudKeySet.has(owId)) || (owHk && cloudKeySet.has(owHk)) || (owKey && cloudKeySet.has(owKey));
      const isCorruptedHealthKitFallback = ow.source === 'HealthKit' && (!ow.calories || String(ow.id || '').startsWith('hk-'));
      if (alreadyInCloud || isCorruptedHealthKitFallback) {
        if (ow.id) {
          await deleteOfflineWorkout(ow.id).catch(() => {});
        }
        continue;
      }
      if (ow.synced === false) {
        unsyncedOffline.push(ow);
      }
    }

    const mergedAllWorkouts = [...unsyncedOffline, ...mappedCloudWorkouts];
    mergedAllWorkouts.sort((a, b) => {
      const tA = Number(a.timestamp) || getWorkoutTimestamp(a) || 0;
      const tB = Number(b.timestamp) || getWorkoutTimestamp(b) || 0;
      return tB - tA;
    });

    state.workouts = mergedAllWorkouts;

    // Cache top 50 workouts for offline fallback
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('pulsefit_cached_workouts', JSON.stringify(state.workouts.slice(0, 50)));
      }
    } catch (_cacheErr) {}

    saveState();
    renderDashboard();
    renderWorkouts();
    if (document.getElementById('view-performance')?.classList.contains('active')) {
      renderPerformanceView();
    }
  } catch (e) {
    console.error('Exception fetching workouts:', e);
    await hydrateWorkoutsFromOfflineFallback();
  }
}

export async function drainOfflineQueue() {
  if (!supabaseClient || !state.currentUser || state.isGuest) return;
  if (!navigator.onLine) return;

  try {
    const offlineWorkouts = await getOfflineWorkouts();
    if (!offlineWorkouts || offlineWorkouts.length === 0) return;

    for (const workout of offlineWorkouts) {
      const rawWorkoutId = workout.id || workout.healthKitId;
      const targetIdLower = String(rawWorkoutId || '').toLowerCase();
      const liveWorkout = (state.workouts || []).find(w =>
        w && String(w.id || w.healthKitId || '').toLowerCase() === targetIdLower
      );

      // Prefer user-edited or AI-evaluated notes from live in-memory state if present
      const liveHasEditedNotes = liveWorkout && (
        liveWorkout.userNotesEdited ||
        (liveWorkout.aiSummary && !isRawStationSequenceNotes(liveWorkout.aiSummary)) ||
        (liveWorkout.notes && !isRawStationSequenceNotes(liveWorkout.notes) && isRawStationSequenceNotes(workout.notes))
      );

      const baseWorkout = liveHasEditedNotes ? { ...workout, ...liveWorkout } : workout;
      let effectiveNotes = baseWorkout.notes;
      let effectiveAiSummary = baseWorkout.aiSummary || baseWorkout.notes;
      let effectiveUserNotesEdited = Boolean(baseWorkout.userNotesEdited);
      let effectiveNotesUpdatedAt = baseWorkout.notesUpdatedAt || null;
      let effectiveStationSeq = baseWorkout.stationSequenceNotes ||
        (isRawStationSequenceNotes(workout.notes) ? workout.notes : null);
      let effectiveStages = baseWorkout.stages || workout.stages;
      let effectiveIsFavorite = Boolean(baseWorkout.isFavorite || false);

      // Guard: if the offline queue item only has raw arrow station notes, check if cloud already has edited notes/AI summary
      let existingCloudMeta = null;
      if (isRawStationSequenceNotes(effectiveNotes) && !effectiveUserNotesEdited && typeof supabaseClient.from === 'function') {
        try {
          const queryBuilder = supabaseClient.from('workouts').select('id, healthkit_raw_metadata, is_favorite');
          if (queryBuilder && typeof queryBuilder.eq === 'function') {
            const eqId = queryBuilder.eq('id', targetIdLower || rawWorkoutId);
            const eqUser = eqId && typeof eqId.eq === 'function' ? eqId.eq('user_id', state.currentUser.id) : null;
            if (eqUser && typeof eqUser.maybeSingle === 'function') {
              const { data: existingRow } = await eqUser.maybeSingle();
              if (existingRow && existingRow.healthkit_raw_metadata) {
                existingCloudMeta = typeof existingRow.healthkit_raw_metadata === 'string'
                  ? JSON.parse(existingRow.healthkit_raw_metadata)
                  : existingRow.healthkit_raw_metadata;
                if (existingRow.is_favorite !== undefined) {
                  effectiveIsFavorite = Boolean(existingRow.is_favorite || effectiveIsFavorite);
                }
              }
            }
          }
        } catch (_) {}
      }

      if (existingCloudMeta && typeof existingCloudMeta === 'object') {
        const cloudResolvedNotes = resolveBestWorkoutNotes(existingCloudMeta, {});
        if (
          existingCloudMeta.userNotesEdited ||
          (existingCloudMeta.aiSummary && !isRawStationSequenceNotes(existingCloudMeta.aiSummary)) ||
          (cloudResolvedNotes && !isRawStationSequenceNotes(cloudResolvedNotes))
        ) {
          effectiveNotes = cloudResolvedNotes;
          effectiveAiSummary = existingCloudMeta.aiSummary || cloudResolvedNotes;
          effectiveUserNotesEdited = true;
          effectiveNotesUpdatedAt = existingCloudMeta.notesUpdatedAt || Date.now();
          if (liveWorkout) {
            liveWorkout.notes = effectiveNotes;
            liveWorkout.aiSummary = effectiveAiSummary;
            liveWorkout.userNotesEdited = true;
            liveWorkout.notesUpdatedAt = effectiveNotesUpdatedAt;
          }
        }
        if (Array.isArray(existingCloudMeta.stages) && existingCloudMeta.stages.length > 0) {
          effectiveStages = existingCloudMeta.stages;
        }
      }

      const mergedMeta = {
        ...(existingCloudMeta && typeof existingCloudMeta === 'object' ? existingCloudMeta : {}),
        ...(workout.rawMetadata && typeof workout.rawMetadata === 'object' ? workout.rawMetadata : {}),
        ...(workout.healthkit_raw_metadata && typeof workout.healthkit_raw_metadata === 'object' ? workout.healthkit_raw_metadata : {}),
        ...(liveWorkout?.rawMetadata && typeof liveWorkout.rawMetadata === 'object' ? liveWorkout.rawMetadata : {}),
        notes: effectiveNotes,
        aiSummary: effectiveAiSummary,
        ...(effectiveUserNotesEdited ? { userNotesEdited: true } : {}),
        ...(effectiveNotesUpdatedAt ? { notesUpdatedAt: effectiveNotesUpdatedAt } : {}),
        ...(effectiveStationSeq ? { stationSequenceNotes: effectiveStationSeq } : {}),
        stages: effectiveStages,
        zones: baseWorkout.zones,
        avgHeartrate: baseWorkout.avgHeartrate,
        maxHeartrate: baseWorkout.maxHeartrate,
        sufferScore: baseWorkout.sufferScore
      };

      const { error } = await supabaseClient.from('workouts').upsert({
        id: targetIdLower || rawWorkoutId,
        user_id: state.currentUser.id,
        created_at: new Date(baseWorkout.timestamp || Date.now()).toISOString(),
        activity_type: baseWorkout.type || 'RoxKit Training',
        duration_seconds: (baseWorkout.duration || 0) * 60,
        start_time: new Date(baseWorkout.timestamp || Date.now()).toISOString(),
        end_time: new Date((baseWorkout.timestamp || Date.now()) + (baseWorkout.duration || 0) * 60000).toISOString(),
        total_energy_burned_kcal: baseWorkout.calories || 0,
        total_distance_meters: Math.round((baseWorkout.distance || 0) * 1000),
        is_favorite: effectiveIsFavorite,
        source_name: baseWorkout.source || 'HealthKit',
        healthkit_raw_metadata: mergedMeta
      }, { onConflict: 'id' });

      if (!error) {
        await deleteOfflineWorkout(workout.id);
        if (targetIdLower && targetIdLower !== String(workout.id)) {
          await deleteOfflineWorkout(targetIdLower);
        }
      }
    }
  } catch (e) {
    console.warn('Failed to drain offline queue:', e);
  }
}

export async function handleIncomingHealthKitWorkouts(workouts) {
  if (!workouts || workouts.length === 0) return;
  const normalizedList = workouts.map(w => (w && w.source === 'HealthKit' && w.id && w.timestamp) ? {
    ...w,
    id: String(w.id).toLowerCase(),
    healthKitId: String(w.healthKitId || w.id).toLowerCase()
  } : normalizeHealthKitWorkout(w));

  const seenIncoming = new Set();
  const dedupedIncoming = [];
  for (const w of normalizedList) {
    const key = String(w.id || w.healthKitId || '').toLowerCase();
    if (key && !seenIncoming.has(key)) {
      seenIncoming.add(key);
      dedupedIncoming.push(w);
    }
  }

  const existingIds = new Set(
    (state.workouts || [])
      .flatMap(w => [w.id, w.healthKitId])
      .filter(Boolean)
      .map(id => String(id).toLowerCase())
  );
  const newWorkouts = dedupedIncoming.filter(w => {
    const idLower = String(w.id || '').toLowerCase();
    const hkIdLower = String(w.healthKitId || '').toLowerCase();
    return !existingIds.has(idLower) && !existingIds.has(hkIdLower);
  });

  if (newWorkouts.length > 0) {
    state.workouts = [...newWorkouts, ...(state.workouts || [])];
    saveState();
    
    for (const w of newWorkouts) {
      await saveOfflineWorkout(w);
    }
    await drainOfflineQueue();

    renderDashboard();
    renderWorkouts();
  }
}

export function handleIncomingHealthSummary(data) {
  if (!data) return;
  const parsed = typeof data === 'string' ? JSON.parse(data) : data;
  const resting = parsed.restingHr ?? parsed.restingHR;
  const sleepHours = parsed.sleepHours ?? (parsed.sleepMinutes ? parsed.sleepMinutes / 60 : undefined);
  const targetDate = parsed.date || parsed.dateString || new Date().toISOString().split('T')[0];

  const currentRec = store.dailyRecovery || {};
  const normalized = {
    date: targetDate,
    metric_date: targetDate,
    source: (currentRec.physicalFeeling || currentRec.mentalFeeling) ? 'hybrid' : 'healthkit'
  };

  if (resting !== undefined && resting !== null && !isNaN(resting)) {
    const rhrVal = Math.round(Number(resting));
    normalized.restingHr = rhrVal;
    normalized.restingHR = rhrVal;
  }
  if (parsed.hrv !== undefined && parsed.hrv !== null && !isNaN(parsed.hrv)) {
    normalized.hrv = Math.round(Number(parsed.hrv));
  }
  if (sleepHours !== undefined && sleepHours !== null && !isNaN(sleepHours)) {
    normalized.sleepHours = Number(Number(sleepHours).toFixed(2));
  }
  if (parsed.sleepScore !== undefined && parsed.sleepScore !== null && !isNaN(parsed.sleepScore)) {
    normalized.sleepScore = Math.min(100, Math.max(0, Math.round(Number(parsed.sleepScore))));
  }
  if (parsed.deepSleepHours !== undefined && parsed.deepSleepHours !== null && !isNaN(parsed.deepSleepHours)) {
    normalized.deepSleepHours = Number(Number(parsed.deepSleepHours).toFixed(2));
  }
  if (parsed.remSleepHours !== undefined && parsed.remSleepHours !== null && !isNaN(parsed.remSleepHours)) {
    normalized.remSleepHours = Number(Number(parsed.remSleepHours).toFixed(2));
  }
  if (parsed.coreSleepHours !== undefined && parsed.coreSleepHours !== null && !isNaN(parsed.coreSleepHours)) {
    normalized.coreSleepHours = Number(Number(parsed.coreSleepHours).toFixed(2));
  }
  if (parsed.awakeHours !== undefined && parsed.awakeHours !== null && !isNaN(parsed.awakeHours)) {
    normalized.awakeHours = Number(Number(parsed.awakeHours).toFixed(2));
  }
  if (parsed.sleepEfficiency !== undefined && parsed.sleepEfficiency !== null && !isNaN(parsed.sleepEfficiency)) {
    normalized.sleepEfficiency = Math.min(100, Math.max(0, Math.round(Number(parsed.sleepEfficiency))));
  }

  const mergedRecovery = {
    ...currentRec,
    ...normalized
  };

  if (mergedRecovery.physicalFeeling || mergedRecovery.mentalFeeling) {
    mergedRecovery.source = 'hybrid';
  }

  store.todayHealth = { ...(store.todayHealth || {}), ...normalized };
  store.dailyRecovery = mergedRecovery;

  saveDailySubjectiveRecovery(mergedRecovery);

  if (state.workouts) {
    state.workouts.forEach(w => {
      const wDate = w.date ? new Date(getWorkoutTimestamp(w)).toISOString().split('T')[0] : '';
      if (wDate === targetDate) {
        if (!w.subjective) w.subjective = {};
        if (normalized.sleepScore !== undefined) w.subjective.sleepScore = normalized.sleepScore;
        if (normalized.hrv !== undefined) w.subjective.hrv = normalized.hrv;
        if (normalized.restingHr !== undefined) w.subjective.restingHr = normalized.restingHr;
      }
    });
    saveState();
  }

  renderPerformanceView();
  renderDashboard();
}

export function handleBiometricStatusChange(success, errorCode) {
  if (success) {
    showToast("Biometric authentication successful!", "success");
    state.isAuthenticated = true;
    saveState();
  } else {
    showToast(`Biometric authentication failed (${errorCode || 'error'})`, "error");
  }
}

export function showToast(message, type = 'success', duration = 1500) {
  if (typeof document === 'undefined') return;
  const container = document.getElementById('toast-container');
  if (!container) return;
  
  const toast = document.createElement('div');
  toast.className = 'glass';
  toast.style.padding = '12px 20px';
  toast.style.borderRadius = 'var(--radius-sm)';
  toast.style.fontSize = '13px';
  toast.style.fontWeight = '600';
  toast.style.color = 'var(--text-primary)';
  toast.style.minWidth = '220px';
  toast.style.boxShadow = '0 8px 32px rgba(0,0,0,0.5)';
  toast.style.opacity = '0';
  toast.style.transform = 'translateY(20px)';
  toast.style.transition = 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)';

  if (type === 'error') {
    toast.style.borderLeft = '4px solid #ef4444';
    toast.style.background = 'rgba(239, 68, 68, 0.15)';
  } else {
    toast.style.borderLeft = '4px solid var(--color-primary)';
    toast.style.background = 'rgba(99, 102, 241, 0.15)';
  }

  // Safe interpolation using escapeHtml
  toast.innerHTML = `<span>${escapeHtml(message)}</span>`;
  container.appendChild(toast);
  
  setTimeout(() => {
    toast.style.opacity = '1';
    toast.style.transform = 'translateY(0)';
  }, 10);

  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transform = 'translateY(-20px)';
    setTimeout(() => toast.remove(), 300);
  }, duration);
}

// Initialise App State
let isAppListenersInitialized = false;

export function initApp() {
  loadStateFromStorage();

  initRouter();
  initDashboardView();
  initPlannerView();
  initWorkoutsView();
  initEditorView();
  initPerformanceView();

  initSettingsView();
  initReleaseNotesView();

  if (typeof __APP_VERSION__ !== 'undefined') {
    const versionSpan = document.getElementById('app-version-placeholder');
    if (versionSpan) versionSpan.innerText = __APP_VERSION__;
  }


  initHealthKitBridge({
    onWorkouts: handleIncomingHealthKitWorkouts,
    onSummary: handleIncomingHealthSummary,
    onLog: (msg) => console.log('[HealthKit]', msg)
  });
  initBiometricBridge({
    onAuthResult: handleBiometricStatusChange
  });

  if (!isAppListenersInitialized) {
    isAppListenersInitialized = true;
    window.addEventListener('pulsefit:healthkit-workouts', async (e) => {
      const detail = e.detail || {};
      const workouts = detail.raw || detail.workouts;
      if (workouts && workouts.length > 0) {
        await handleIncomingHealthKitWorkouts(workouts);
      }
    });
    window.addEventListener('pulsefit:healthkit-summary', (e) => {
      if (e.detail) handleIncomingHealthSummary(e.detail);
    });
    window.addEventListener('pulsefit:biometric-status', (e) => {
      if (e.detail) handleBiometricStatusChange(e.detail.success, e.detail.errorCode);
    });

    // Register Service Worker for Offline PWA Caching
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator && window.location.protocol.startsWith('http')) {
      const registerSW = () => {
        navigator.serviceWorker.register('/sw.js').catch(err => {
          console.warn('[PWA] ServiceWorker registration failed:', err);
        });
      };
      if (document.readyState === 'complete') {
        registerSW();
      } else {
        window.addEventListener('load', registerSW);
      }
    }
  }

  updateWeekUI();
  renderDashboard();
  renderWorkouts();
  renderAchievements();

  // Offline resilience interval
  if (typeof window !== 'undefined') {
    window.addEventListener('online', drainOfflineQueue);
    setInterval(drainOfflineQueue, 60000); // drain every 1 min
  }
}

export function bootApplication() {
  initErrorTelemetry();
  if (supabaseClient) {
    let authMode = 'login';
    const linkAuthToggle = document.getElementById('link-auth-toggle');
    const authNameGroup = document.getElementById('auth-name-group');
    const authConfirmGroup = document.getElementById('auth-confirm-group');
    const authSubtitle = document.getElementById('auth-subtitle');
    const btnAuthSubmit = document.getElementById('btn-auth-submit');
    const authToggleLabel = document.getElementById('auth-toggle-label');
    const authError = document.getElementById('auth-error');
    const inputName = document.getElementById('auth-name');
    const inputEmail = document.getElementById('auth-email');
    const inputPassword = document.getElementById('auth-password');
    const btnSettingsLogout = document.getElementById('btn-settings-logout');
    const btnAuthGuest = document.getElementById('btn-auth-guest');
    
    // New Wizard Elements
    const authStep1 = document.getElementById('auth-step-1');
    const authStep2 = document.getElementById('auth-step-2');
    const authStep3 = document.getElementById('auth-step-3');
    const authActionsStep1 = document.getElementById('auth-actions-step-1');
    const btnAuthBack = document.getElementById('btn-auth-back');
    const btnAuthFinish = document.getElementById('btn-auth-finish');
    const verifyEmailDisplay = document.getElementById('verify-email-display');
    const btnBackToLogin = document.getElementById('btn-back-to-login');
    const authWizardCard = document.getElementById('auth-wizard-card');
    
    // Forgot password elements
    const linkForgotPassword = document.getElementById('link-forgot-password');
    const forgotPasswordModal = document.getElementById('forgot-password-modal');
    const forgotPasswordForm = document.getElementById('forgot-password-form');
    const btnForgotPasswordCancel = document.getElementById('btn-forgot-password-cancel');
    const inputForgotPasswordEmail = document.getElementById('forgot-password-email');

    if (linkAuthToggle) {
      linkAuthToggle.addEventListener('click', (e) => {
        e.preventDefault();
        if (authError) authError.style.display = 'none';
        if (authMode === 'login') {
          authMode = 'signup';
          if (authNameGroup) authNameGroup.style.display = 'flex';
          if (authConfirmGroup) authConfirmGroup.style.display = 'flex';
          if (authSubtitle) authSubtitle.innerText = 'Create an account to get started';
          if (btnAuthSubmit) btnAuthSubmit.innerText = 'Continue';
          if (authToggleLabel) authToggleLabel.innerText = 'Already have an account?';
          if (linkAuthToggle) linkAuthToggle.innerText = 'Login';
          if (linkForgotPassword) linkForgotPassword.style.display = 'none';
        } else {
          authMode = 'login';
          if (authNameGroup) authNameGroup.style.display = 'none';
          if (authConfirmGroup) authConfirmGroup.style.display = 'none';
          if (authSubtitle) authSubtitle.innerText = 'Sign in to access your dashboard';
          if (btnAuthSubmit) btnAuthSubmit.innerText = 'Login';
          if (authToggleLabel) authToggleLabel.innerText = "Don't have an account?";
          if (linkAuthToggle) linkAuthToggle.innerText = 'Sign Up';
          if (linkForgotPassword) linkForgotPassword.style.display = 'inline';
        }
      });
    }
    
    // Step 1 to Step 2 Transition
    if (btnAuthSubmit) {
      btnAuthSubmit.addEventListener('click', async (e) => {
        e.preventDefault();
        if (authError) authError.style.display = 'none';
        const email = inputEmail?.value.trim() || '';
        const password = inputPassword?.value || '';
        
        if (!email || !password) {
           if (authError) { authError.innerText = "Please fill in all required fields."; authError.style.display = 'block'; }
           return;
        }

        try {
          if (authMode === 'login') {
            const originalText = btnAuthSubmit.innerText;
            btnAuthSubmit.innerText = 'Logging in...';
            btnAuthSubmit.disabled = true;
            try {
              await handleLogin(email, password);
            } finally {
              btnAuthSubmit.innerText = originalText;
              btnAuthSubmit.disabled = false;
            }
          } else {
            // Pre-validation before moving to step 2
            const confirmPassword = document.getElementById('auth-confirm-password')?.value || '';
            if (password !== confirmPassword) {
              throw new Error("Passwords do not match.");
            }
            if (password.length < 6) {
              throw new Error("Password must be at least 6 characters.");
            }
            // Move to Step 2
            if (authStep1) authStep1.style.display = 'none';
            if (authActionsStep1) authActionsStep1.style.display = 'none';
            if (authStep2) authStep2.style.display = 'flex';
          }
        } catch (err) {
          console.error("Auth action failed:", err);
          if (authError && err.message) {
            authError.innerText = err.message;
            authError.style.display = 'block';
          }
        }
      });
    }

    // Step 2 to Step 3 (Finish Sign Up)
    if (btnAuthFinish) {
      btnAuthFinish.addEventListener('click', async (e) => {
        e.preventDefault();
        if (authError) authError.style.display = 'none';
        const email = inputEmail?.value.trim() || '';
        const password = inputPassword?.value || '';
        const fullName = inputName?.value.trim() || '';
        
        const metadata = {
          full_name: fullName,
          dob: document.getElementById('auth-dob')?.value || null,
          gender: document.getElementById('auth-gender')?.value || null,
          weight_kg: document.getElementById('auth-weight')?.value || null,
          height_cm: document.getElementById('auth-height')?.value || null,
          fitness_level: document.getElementById('auth-fitness-level')?.value || null,
          primary_goal: document.getElementById('auth-goal')?.value || null
        };

        try {
          btnAuthFinish.innerText = 'Creating...';
          btnAuthFinish.disabled = true;
          await handleSignUp(email, password, metadata);
          
          // Move to Step 3
          if (authWizardCard) authWizardCard.style.display = 'none';
          if (authStep3) {
             authStep3.style.display = 'flex';
             if (verifyEmailDisplay) verifyEmailDisplay.innerText = email;
          }
        } catch (err) {
          console.error("Sign up failed:", err);
          if (authError && err.message) {
            authError.innerText = err.message;
            authError.style.display = 'block';
          }
        } finally {
          btnAuthFinish.innerText = 'Create Account';
          btnAuthFinish.disabled = false;
        }
      });
    }
    
    // Back to Step 1
    if (btnAuthBack) {
      btnAuthBack.addEventListener('click', (e) => {
        e.preventDefault();
        if (authStep2) authStep2.style.display = 'none';
        if (authStep1) authStep1.style.display = 'flex';
        if (authActionsStep1) authActionsStep1.style.display = 'block';
      });
    }

    // Return to Login from Step 3
    if (btnBackToLogin) {
      btnBackToLogin.addEventListener('click', (e) => {
        e.preventDefault();
        if (authStep3) authStep3.style.display = 'none';
        if (authWizardCard) authWizardCard.style.display = 'flex';
        if (authStep2) authStep2.style.display = 'none';
        if (authStep1) authStep1.style.display = 'flex';
        if (authActionsStep1) authActionsStep1.style.display = 'block';
        if (linkAuthToggle) linkAuthToggle.click(); // Switch to login mode
      });
    }

    // Forgot Password Flow
    if (linkForgotPassword) {
      linkForgotPassword.addEventListener('click', (e) => {
        e.preventDefault();
        if (forgotPasswordModal) {
          forgotPasswordModal.style.display = 'flex';
          if (inputForgotPasswordEmail && inputEmail) {
            inputForgotPasswordEmail.value = inputEmail.value;
          }
        }
      });
    }

    if (btnForgotPasswordCancel) {
      btnForgotPasswordCancel.addEventListener('click', (e) => {
        e.preventDefault();
        if (forgotPasswordModal) forgotPasswordModal.style.display = 'none';
      });
    }

    if (forgotPasswordForm) {
      forgotPasswordForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const email = inputForgotPasswordEmail?.value.trim() || '';
        if (!email) return;
        const submitBtn = document.getElementById('btn-forgot-password-submit');
        if (submitBtn) {
           submitBtn.innerText = 'Sending...';
           submitBtn.disabled = true;
        }
        try {
          await handlePasswordReset(email);
        } catch (err) {
          console.error('Password reset error:', err);
        } finally {
          if (submitBtn) {
             submitBtn.innerText = 'Send Link';
             submitBtn.disabled = false;
          }
        }
      });
    }
    if (btnSettingsLogout) btnSettingsLogout.addEventListener('click', () => handleLogout());
    if (btnAuthGuest) btnAuthGuest.addEventListener('click', () => handleGuestMode());

    const togglePasswordBtn = document.getElementById('toggle-auth-password');
    if (togglePasswordBtn) {
      togglePasswordBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const type = inputPassword.getAttribute('type') === 'password' ? 'text' : 'password';
        inputPassword.setAttribute('type', type);
      });
    }

    const toggleConfirmPasswordBtn = document.getElementById('toggle-auth-confirm-password');
    const inputConfirmPassword = document.getElementById('auth-confirm-password');
    if (toggleConfirmPasswordBtn && inputConfirmPassword) {
      toggleConfirmPasswordBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const type = inputConfirmPassword.getAttribute('type') === 'password' ? 'text' : 'password';
        inputConfirmPassword.setAttribute('type', type);
      });
    }

    const handleAuthSession = async (event, session) => {
      if (session) {
        if (!state.currentUser || state.currentUser.id !== session.user.id) {
          resetStore();
        }
        state.currentUser = session.user;
        state.isGuest = false;
        loadStateFromStorage();

        if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.authHandler) {
          let alreadySyncedUid = null;
          let alreadySyncedToken = null;
          if (typeof sessionStorage !== 'undefined') {
            try {
              alreadySyncedUid = sessionStorage.getItem('pulsefit_ios_auth_synced_uid');
              alreadySyncedToken = sessionStorage.getItem('pulsefit_ios_auth_synced_token');
            } catch (_) {}
          }
          const latestJwt = (typeof localStorage !== 'undefined' ? localStorage.getItem('pulsefit_jwt') : null) || session.access_token;
          const latestRefresh = (typeof localStorage !== 'undefined' ? localStorage.getItem('pulsefit_refresh_token') : null) || session.refresh_token || '';

          if (alreadySyncedUid !== session.user.id || (latestJwt && alreadySyncedToken !== latestJwt)) {
            if (typeof sessionStorage !== 'undefined') {
              try {
                sessionStorage.setItem('pulsefit_ios_auth_synced_uid', session.user.id);
                if (latestJwt) sessionStorage.setItem('pulsefit_ios_auth_synced_token', latestJwt);
              } catch (_) {}
            }
            window.webkit.messageHandlers.authHandler.postMessage({
              action: 'login',
              userId: session.user.id,
              token: latestJwt,
              refreshToken: latestRefresh
            });
          }
        }

        await ensureUserProfileExists(session.user);

        // Hydrate today's recovery from Supabase daily_recovery
        try {
          const todayDateStr = new Date().toISOString().split('T')[0];
          const { data: recoveryRow, error: recError } = await supabaseClient
            .from('daily_recovery')
            .select('*')
            .eq('user_id', session.user.id)
            .eq('metric_date', todayDateStr)
            .maybeSingle();

          if (!recError && recoveryRow) {
            const normalizedRec = {
              restingHr: recoveryRow.resting_hr ?? undefined,
              restingHR: recoveryRow.resting_hr ?? undefined,
              hrv: recoveryRow.hrv ?? undefined,
              sleepHours: recoveryRow.sleep_hours ? Number(recoveryRow.sleep_hours) : undefined,
              sleepScore: recoveryRow.sleep_score ?? undefined,
              physicalFeeling: recoveryRow.physical_feeling ?? undefined,
              mentalFeeling: recoveryRow.mental_feeling ?? undefined,
              readinessScore: recoveryRow.readiness_score ?? undefined,
              source: recoveryRow.source || 'healthkit',
              date: recoveryRow.metric_date,
              metric_date: recoveryRow.metric_date
            };
            store.dailyRecovery = normalizedRec;
            store.todayHealth = normalizedRec;
            saveDailySubjectiveRecovery(normalizedRec);
          }
        } catch (recErr) {
          console.warn('[Recovery] Failed to hydrate daily recovery from Supabase:', recErr);
        }

        // Request fresh HealthKit summary from native iOS companion
        requestHealthKitSummary();

        const authView = document.getElementById('view-auth');
        const mainApp = document.getElementById('main-app-container');
        if (authView) authView.style.display = 'none';
        if (mainApp) mainApp.style.display = 'grid';

        await fetchUserWorkouts();
        drainOfflineQueue().catch((e) => console.warn('Offline queue drain warning:', e));
        initApp();
        showView('view-dashboard');
      } else {
        const isCompanion = Boolean(
          typeof window !== 'undefined' &&
          window.webkit &&
          window.webkit.messageHandlers &&
          window.webkit.messageHandlers.authHandler
        );
        const hasInjectedJwt = Boolean(
          typeof localStorage !== 'undefined' &&
          (localStorage.getItem('pulsefit_jwt') || localStorage.getItem('pulsefit_session'))
        );
        if (!isCompanion || !hasInjectedJwt) {
          clearAllLocalUserData();
        }
        const authView = document.getElementById('view-auth');
        const mainApp = document.getElementById('main-app-container');
        if (authView) authView.style.display = 'flex';
        if (mainApp) mainApp.style.display = 'none';
      }
    };

    if (typeof window !== 'undefined') {
      window.handleAuthSession = handleAuthSession;
      window.addEventListener('pulsefit:auth-synced', async (e) => {
        const session = e?.detail?.session;
        if (!session || !session.user) return;
        const authView = document.getElementById('view-auth');
        const mainApp = document.getElementById('main-app-container');
        const isAuthVisible = authView ? authView.style.display !== 'none' : false;
        const isMainHidden = mainApp ? (mainApp.style.display === 'none' || mainApp.style.display === '') : true;
        const isUserMissingOrMismatched = !state.currentUser || (state.currentUser.id !== session.user.id);

        if (isAuthVisible || isMainHidden || isUserMissingOrMismatched) {
          isAppInitialized = true;
          await handleAuthSession('SIGNED_IN', session);
        }
      });
    }

    let isAppInitialized = false;
    supabaseClient.auth.onAuthStateChange(async (event, session) => {
      if (event === 'INITIAL_SESSION') return;
      if (event === 'TOKEN_REFRESHED' && session && session.user) {
        if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.authHandler) {
          if (typeof sessionStorage !== 'undefined') {
            try {
              sessionStorage.setItem('pulsefit_ios_auth_synced_uid', session.user.id);
              if (session.access_token) sessionStorage.setItem('pulsefit_ios_auth_synced_token', session.access_token);
            } catch (_) {}
          }
          window.webkit.messageHandlers.authHandler.postMessage({
            action: 'login',
            userId: session.user.id,
            token: session.access_token,
            refreshToken: session.refresh_token || ''
          });
        }
        return;
      }
      if (event === 'SIGNED_IN') isAppInitialized = false;
      if (!isAppInitialized || event === 'SIGNED_OUT') {
        isAppInitialized = true;
        await handleAuthSession(event, session);
      }
    });

    supabaseClient.auth.getSession().then(({ data: { session } }) => {
      if (!isAppInitialized) {
        isAppInitialized = true;
        handleAuthSession('INITIAL_SESSION', session);
      }
    });
  } else {
    initApp();
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    window.addEventListener('DOMContentLoaded', bootApplication);
  } else {
    bootApplication();
  }
}

export {
  saveWorkoutNotes,
  generateAISummary,
  setupExpandedWorkoutNotesModal,
  initDictateAssistant,
  openExpandedHeartRateModal,
  setupExpandedHeartRateModal,
  openShareCardModal,
  setupShareCardModal,
  renderStagesHeartRateChart,
  renderExpandedStagesHeartRateChart,
  updateExpandedSegmentHUD,
  renderHeartRateZonesUI,
  renderHeartRateChart,
  renderExpandedHeartRateChart,
  runAIEvaluation,
  processVoiceCommandWithGemini,
  updateStravaPostButtonState,
  updateWorkoutInCloud,
  handlePostWorkoutToStrava,
  setupStrava,
  updateStravaUI,
  exchangeStravaCode,
  refreshStravaToken,
  syncStravaActivities,
  scrubPII,
  reportError,
  initErrorTelemetry,
  getQueuedTelemetryErrors,
  clearQueuedTelemetryErrors,
  openPlanRoutineModal,
  closePlanRoutineModal,
  addPlanSplit,
  loadHyroxPresetIntoPlanner,
  clearPlanSplits,
  updatePlanSplitField,
  stepPlanSplitField,
  movePlanSplit,
  duplicatePlanSplit,
  removePlanSplit,
  recalculatePlanTotals,
  savePlannedWorkout,
  deletePlannedWorkout,
  sendPlannedWorkoutToWatch,
  renderSavedPlannedWorkouts,
  generateWorkoutRecommendation,
  initPlannerView,
  getDraftPlanSplits,
  setDraftPlanSplits,
  getCurrentEditingPlanId
};

